import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FiRefreshCw } from 'react-icons/fi';
import httpClient from 'utils/httpClient';
import { getPortalScope, PORTAL_SCOPE_CHANGE_EVENT } from 'utils/portalScope';
import { getRetailReportByPath } from './reportConfigs';
import { buildAnalytics } from './retailAnalyticsData';
import { RETAIL_ANALYTICS_LAYOUTS } from './retailAnalyticsLayouts';
import { RetailReportPanel } from './RetailReportPage';
import SalesReportFilters, { getSalesReportQuery } from './SalesReportFilters';
import { ReportContextBar, ReportKpiGrid, ReportPageHeader, ReportPageLayout, ReportState } from './ReportComponents';
import './retailReports.scss';

const RetailAnalyticsPage = () => {
  const { pathname, search } = useLocation();
  const report = getRetailReportByPath(pathname);
  const query = useMemo(() => getSalesReportQuery(search), [search]);
  const [scope, setScope] = useState(() => getPortalScope());
  const [refresh, setRefresh] = useState(0);
  const [horizon, setHorizon] = useState(14);
  const [state, setState] = useState({ loading: false, error: '', data: null, key: '' });
  const key = JSON.stringify([report.id, query.date_from, query.date_to, scope.company_id, scope.business_id, scope.store_id, refresh]);
  useEffect(() => {
    const listener = (event) => setScope(event.detail || getPortalScope());
    window.addEventListener(PORTAL_SCOPE_CHANGE_EVENT, listener);
    return () => window.removeEventListener(PORTAL_SCOPE_CHANGE_EVENT, listener);
  }, []);
  useEffect(() => {
    let current = true;
    if (!scope.store_id) { setState({ loading: false, error: '', data: null, key }); return undefined; }
    setState({ loading: true, error: '', data: null, key });
    httpClient.get(`/retail-report/analytics/${report.id}`, { params: { date_from: query.date_from, date_to: query.date_to } })
      .then((data) => { if (current) setState({ loading: false, error: '', data, key }); })
      .catch((error) => { if (current) setState({ loading: false, error: error?.message || 'Không tải được báo cáo.', data: null, key }); });
    return () => { current = false; };
  }, [key, query.date_from, query.date_to, report.id, scope.store_id]);
  const data = state.key === key ? state.data : null;
  const loading = Boolean(scope.store_id) && (state.key !== key || state.loading);
  const content = useMemo(() => buildAnalytics(report.id, data, { horizon, storeName: scope.store_name || 'Chưa chọn cửa hàng' }), [report.id, data, horizon, scope.store_name]);
  return <ReportPageLayout reportId={report.id}>
    <ReportPageHeader title={report.name} breadcrumb='Bán hàng / Phân tích bán lẻ'
      description={RETAIL_ANALYTICS_LAYOUTS[report.id]?.subtitle}
      actions={<button className='bc_rr_action_button is-secondary' disabled={loading || !scope.store_id} onClick={() => setRefresh(refresh + 1)}><FiRefreshCw /> Nạp lại số liệu</button>} />
    <ReportContextBar items={content.filters} />
    <SalesReportFilters loading={loading} />
    {report.id === 'replenishment-forecast' ? <label className='bc_rr_toolbar'>Chu kỳ mua
      <select aria-label='Chu kỳ mua' value={horizon} onChange={(event) => setHorizon(Number(event.target.value))}><option value={14}>14 ngày tới</option><option value={30}>30 ngày tới</option></select>
    </label> : null}
    {!scope.store_id ? <ReportState>Chọn cửa hàng trên thanh điều hướng để xem báo cáo.</ReportState> : null}
    {loading ? <ReportState type='loading'>Đang tổng hợp số liệu thực tế...</ReportState> : null}
    {state.key === key && state.error ? <ReportState type='error'>{state.error}</ReportState> : null}
    {data ? <>
      <ReportKpiGrid metrics={content.metrics} />
      <section className='bc_rr_panel_grid' aria-label='Phân tích chi tiết'>
        {content.panels.map((panel, index) => <RetailReportPanel key={`${key}-${index}`} panel={panel} />)}
      </section>
      <details className='bc_rr_panel'><summary className='bc_rr_panel_head'>Phạm vi và cách tính</summary><div className='bc_rr_panel_body'>{content.notes.map((note) => <p key={note}>{note}</p>)}<small>Cập nhật: {new Date(data.loaded_at).toLocaleString('vi-VN')}</small></div></details>
    </> : null}
  </ReportPageLayout>;
};
export default RetailAnalyticsPage;
